/* VELUPIA buy box — Pack × Color radios → one variant id; gallery; Ajax add-to-cart (vel-cart-add.js);
   mobile sticky bar whose pack chips drive the pack radios. Without JS the native form posts to /cart/add. */
import { addForm } from './vel-cart-add.js';

const bound = new WeakSet();

function initGallery(gal) {
  const strip = gal.querySelector('.vbx-gal__slides');
  const thumbs = [...gal.querySelectorAll('[data-vbx-thumb]')];
  const count = gal.querySelector('[data-vbx-count]');
  const slides = [...gal.querySelectorAll('.vbx-gal__slide')];
  if (!strip || !slides.length) return null;
  let current = 0;
  const setActive = (i) => {
    current = i;
    thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
    if (count) count.textContent = String(i + 1);
    const t = thumbs[i];
    if (t) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  const go = (i, smooth = true) => {
    const n = (i + slides.length) % slides.length;
    strip.scrollTo({ left: strip.clientWidth * n, behavior: smooth ? 'smooth' : 'auto' });
    setActive(n);
  };
  thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));
  gal.querySelectorAll('[data-vbx-step]').forEach((b) => b.addEventListener('click', () => go(current + Number(b.dataset.vbxStep))));
  strip.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(current + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(current - 1); }
  });
  let raf;
  strip.addEventListener('scroll', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const i = Math.round(strip.scrollLeft / strip.clientWidth);
      if (i !== current) setActive(i);
    });
  }, { passive: true });
  const map = JSON.parse(gal.querySelector('[data-vbx-media-map]')?.textContent || '{}');
  const goToMedia = (id) => {
    const i = slides.findIndex((sl) => sl.dataset.mediaId === String(id));
    if (i >= 0) go(i);
    return i >= 0;
  };
  return { go, goToMedia, map };
}

/** Delivery window from business days entered in the section settings (Mon–Fri, no holidays). */
function initEta(rootEl) {
  const el = rootEl.querySelector('[data-vbx-eta]');
  if (!el) return;
  const addBusinessDays = (date, n) => {
    const d = new Date(date);
    while (n > 0) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) n -= 1; }
    return d;
  };
  const fmt = new Intl.DateTimeFormat(el.dataset.lang || document.documentElement.lang || 'es', { weekday: 'short', day: '2-digit', month: '2-digit' });
  const today = new Date();
  const from = addBusinessDays(today, Number(el.dataset.min));
  const to = addBusinessDays(today, Number(el.dataset.max));
  el.querySelector('[data-vbx-eta-text]').textContent = `${fmt.format(from)} – ${fmt.format(to)}`;
  el.hidden = false;
}

function init(scope = document) {
  scope.querySelectorAll('[data-vbx]').forEach((rootEl) => {
    if (bound.has(rootEl)) return;
    bound.add(rootEl);
    initEta(rootEl);
    const gal = rootEl.querySelector('[data-vbx-gallery]');
    const gallery = gal ? initGallery(gal) : null;

    const form = rootEl.querySelector('form[data-vbx-form]');
    const json = rootEl.querySelector('[data-vbx-variants]');
    const input = rootEl.querySelector('[data-vbx-id]');
    if (form && json && input) {
      const variants = JSON.parse(json.textContent);
      rootEl.querySelectorAll('[data-vbx-js]').forEach((el) => { el.hidden = false; });
      input.disabled = false;
      const atc = rootEl.querySelector('[data-vbx-atc]');
      const atcPrice = rootEl.querySelector('[data-vbx-atc-price]');
      const stickyBtn = rootEl.querySelector('[data-vbx-sticky-btn]');
      const stickySets = [...rootEl.querySelectorAll('[data-vbx-sticky-set]')];
      const stickyInfo = rootEl.querySelector('[data-vbx-sticky-info]');
      const colorLabel = rootEl.querySelector('[data-vbx-color-label]');
      const msg = rootEl.querySelector('[data-vbx-msg]');
      const checked = (n) => rootEl.querySelector(`input[data-vbx-opt="${n}"]:checked`)?.value ?? null;

      const update = (moveGallery) => {
        const set = checked(1);
        const colorInputs = [...rootEl.querySelectorAll('input[data-vbx-opt="2"]')];
        // Only the colours that exist for this pack (e.g. "2 × Rosa" for the Dúo only).
        colorInputs.forEach((c) => {
          const v = variants.find((x) => x.o1 === set && x.o2 === c.value);
          c.closest('.vbx-swatch').hidden = !v;
          c.disabled = !v || !v.available;
        });
        let color = checked(2);
        let v = variants.find((x) => x.o1 === set && (colorInputs.length === 0 || x.o2 === color));
        if (!v || !v.available) {
          v = variants.find((x) => x.o1 === set && x.available) || variants.find((x) => x.o1 === set);
          const c = colorInputs.find((i) => i.value === v?.o2);
          if (c) c.checked = true;
          color = v?.o2 ?? null;
        }
        if (!v) return;
        input.value = v.id;
        [atc, stickyBtn].forEach((b) => { if (b) b.disabled = !v.available; });
        if (atcPrice) atcPrice.textContent = v.price;
        stickySets.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.vbxStickySet === set)));
        if (stickyInfo) {
          const chip = stickySets.find((b) => b.dataset.vbxStickySet === set);
          const save = chip?.dataset.save;
          stickyInfo.textContent = save || stickyInfo.dataset.fallback;
          stickyInfo.classList.toggle('is-saving', !!save);
        }
        if (colorLabel && color) colorLabel.textContent = color;
        rootEl.querySelectorAll('[data-vbx-incl]').forEach((el) => { el.hidden = el.dataset.vbxIncl !== set; });
        // Gallery: a real variant image wins; otherwise the image mapped to the pack or colour that was just picked.
        if (moveGallery && gallery) {
          const mapped = moveGallery === 'colour' ? gallery.map.colors?.[color] : gallery.map.sets?.[set];
          if (!(v.media && gallery.goToMedia(v.media)) && mapped) gallery.goToMedia(mapped);
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      };
      rootEl.addEventListener('change', (e) => {
        if (e.target.matches('input[data-vbx-opt]')) update(e.target.dataset.vbxOpt === '2' ? 'colour' : 'set');
      });
      update(false);

      // Sticky chips pick the pack through the real radios so the form stays the single source of truth.
      // The gallery is left alone: moving it would scroll the page back up and hide the sticky bar.
      stickySets.forEach((b) => b.addEventListener('click', () => {
        const radio = [...rootEl.querySelectorAll('input[data-vbx-opt="1"]')].find((r) => r.value === b.dataset.vbxStickySet);
        if (!radio || radio.disabled || radio.checked) return;
        radio.checked = true;
        update(false);
      }));

      form.addEventListener('submit', (e) => {
        if (!window.fetch) return;
        e.preventDefault();
        addForm(form, atc, msg);
      });

      const sticky = rootEl.querySelector('[data-vbx-sticky]');
      if (sticky && atc && 'IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => {
          const show = !en.isIntersecting && en.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', String(!show));
          [stickyBtn, ...stickySets].forEach((b) => { if (b) b.tabIndex = show ? 0 : -1; });
        }).observe(atc);
      }
    }
  });
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
